import { IOneTimeInvoiceFormSchemaWithOfferings } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { MBox } from '../../../../components/Monetize';
import { QuoteBasicInfoLine } from '../../../../components/Quotes/QuoteBasicInfoLine';
import { QuoteInlineEditableTextField } from '../../../../components/Quotes/QuoteInlineEditableTextField';

const BUTTON_PROPS = { ml: '-3' };
const PREVIEW_PROPS_NON_EDIT = { justifyContent: 'flex-start' };

type ReferenceNumberField =
  | 'purchaseOrderNumber'
  | 'vatNumber'
  | 'registrationNumber';

const REFERENCE_NUMBER_ROWS: Array<{
  name: ReferenceNumberField;
  title: string;
  maxLength?: number;
}> = [
  // The invoice schema caps the PO number at 255 characters
  { name: 'purchaseOrderNumber', title: 'PO Number', maxLength: 255 },
  { name: 'vatNumber', title: 'VAT Number' },
  { name: 'registrationNumber', title: 'Registration Number' },
];

const ReferenceNumberRow = ({
  name,
  title,
  maxLength,
}: {
  name: ReferenceNumberField;
  title: string;
  maxLength?: number;
}) => {
  const { control, setValue } =
    useFormContext<IOneTimeInvoiceFormSchemaWithOfferings>();
  const value = useWatch({ control, name });

  const handleSave = useCallback(
    async (next: Maybe<string>) => {
      setValue(name, next, { shouldDirty: true, shouldValidate: true });
    },
    [name, setValue],
  );

  return (
    <QuoteBasicInfoLine title={title}>
      <MBox mr="-3">
        <QuoteInlineEditableTextField
          value={value}
          placeholder={title}
          maxLength={maxLength}
          onSave={handleSave}
          buttonProps={BUTTON_PROPS}
          previewPropsNonEdit={PREVIEW_PROPS_NON_EDIT}
        />
      </MBox>
    </QuoteBasicInfoLine>
  );
};

/**
 * Third column of the invoice setup card — the reference numbers carried onto
 * the invoice, using the same add-then-edit control as the quote details panel.
 */
export const OneTimeInvoiceBasicDetails = () => (
  <MBox>
    {REFERENCE_NUMBER_ROWS.map((row) => (
      <ReferenceNumberRow key={row.name} {...row} />
    ))}
  </MBox>
);
